import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Modal, Spin, Tag, Table } from "antd";
import { RiEditFill, RiDeleteBin2Fill } from "react-icons/ri";
import {
  getAllAnnouncements,
  deleteAnnouncementById,
} from "@api/notification/announcement";
import {
  SuccessNotification,
  ErrorNotification,
  AnnouncementCreateModal,
  AnnouncementUpdateModal,
} from "@components";

const AnnouncementsList = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [announcementId, setAnnouncementId] = useState("");

  // Fetch all announcements
  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const { data } = await getAllAnnouncements();
      setAnnouncements(data);
    } catch (error) {
      console.error("Error fetching announcements", error);
    }
    setLoading(false);
  };

  // Effect to fetch announcements on component mount
  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Handle create announcement modal
  const handleCreateModalClick = () => {
    setCreateModalOpen(!createModalOpen);
  };

  // Handle update announcement modal
  const handleUpdateModalClick = (id) => {
    setAnnouncementId(id);
    setUpdateModalOpen(!updateModalOpen);
  };

  // Handle announcement delete
  const handleAnnouncementDelete = (id) => {
    // Confirm delete
    Modal.confirm({
      title: "Are you sure you want to delete this announcement?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteAnnouncementById(id);
          fetchAnnouncements();
          SuccessNotification("Announcement deleted successfully");
        } catch (error) {
          ErrorNotification("Error deleting announcement");
        }
      },
    });
  };

  // Define the columns for the table
  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Content",
      dataIndex: "content",
      key: "content",
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (active) => (
        <Tag color={active ? "green" : "red"}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Start",
      dataIndex: "start",
      key: "start",
      render: (start) => new Date(start).toLocaleDateString(),
    },
    {
      title: "End",
      dataIndex: "end",
      key: "end",
      render: (end) => new Date(end).toLocaleDateString(),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          <RiEditFill onClick={() => handleUpdateModalClick(record._id)} />
          <span style={{ margin: "0 10px" }} />
          <RiDeleteBin2Fill
            onClick={() => handleAnnouncementDelete(record._id)}
          />
        </>
      ),
    },
  ];

  return (
    <>
      {/* Create Announcement Modal */}
      {createModalOpen && (
        <AnnouncementCreateModal
          open={createModalOpen}
          handleModalClick={handleCreateModalClick}
          fetchAnnouncements={fetchAnnouncements}
        />
      )}

      {/* Update Announcement Modal */}
      {updateModalOpen && (
        <AnnouncementUpdateModal
          open={updateModalOpen}
          handleModalClick={handleUpdateModalClick}
          fetchAnnouncements={fetchAnnouncements}
          announcementId={announcementId}
        />
      )}

      <Card style={{ borderRadius: "0", height: "100vh" }}>
        <Row>
          <Col span={24}>
            <Row justify="space-between" style={{ marginBottom: 16 }}>
              <h3>Announcements List</h3>
              <Button type="primary" onClick={handleCreateModalClick}>
                Create Announcement
              </Button>
            </Row>

            {/* Announcements table */}
            {loading ? (
              <Spin
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 50,
                }}
              />
            ) : (
              <Table
                columns={columns}
                dataSource={announcements}
                rowKey={(record) => record._id}
                pagination={false} // Disable pagination
              />
            )}
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default AnnouncementsList;
